import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Roles con acceso total automático
const FULL_ACCESS_ROLES = ['super_admin', 'admin'];

/**
 * AuthProvider — autenticación basada en cookie httpOnly.
 *
 * El token JWT vive únicamente en una cookie httpOnly seteada por el backend
 * en `/api/auth/login`. El JS NUNCA tiene acceso al token (mitiga XSS),
 * por lo que aquí solo guardamos información de usuario y un flag de sesión.
 *
 * En cada arranque consultamos /api/auth/me: si hay cookie válida, regresa el
 * usuario; si no, queda como anónimo.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Bandera "tenemos sesión", actualizada según la respuesta de /me y login/logout.
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      setAuthenticated(true);
    } catch (err) {
      // 401 = no hay cookie válida (anonymous). Otros errores se loguean.
      if (err?.response?.status !== 401) {
        console.error('Auth check error:', err);
      }
      setUser(null);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    // El backend setea la cookie httpOnly automáticamente en la respuesta.
    const res = await api.post('/auth/login', { email, password });
    const userData = res.data.user;
    setUser(userData);
    setAuthenticated(true);
    return userData;
  };

  const register = async (nombre, email, password) => {
    const res = await api.post('/auth/register', { nombre, email, password });
    // Nuevo flujo: el registro queda pendiente de aprobación. NO autenticamos.
    if (res.data?.pendiente_aprobacion) {
      return { pendiente_aprobacion: true, message: res.data.message };
    }
    // Compat con la respuesta vieja (por si el backend aún no se actualizó).
    const userData = res.data.user;
    setUser(userData);
    setAuthenticated(true);
    return userData;
  };

  const logout = useCallback(async () => {
    // Pedimos al backend que limpie la cookie. Aunque falle, limpiamos estado local.
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.warn('Logout request failed (continuing):', err?.message || err);
    }
    setUser(null);
    setAuthenticated(false);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authenticated) return;
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  }, [authenticated]);

  // Helpers de permisos
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin';

  // Modo "Conteo Ciego": marca especial dentro de `modulos_permitidos` que
  // oculta al usuario las cifras del sistema (existencias globales y columnas
  // Sistema/Diferencia en los conteos). Super Admin NUNCA se ve afectado.
  const isConteoCiego = !isSuperAdmin && (user?.modulos_permitidos || []).includes('_conteo_ciego');

  const hasModuleAccess = useCallback((moduleKey) => {
    if (!user) return false;
    if (FULL_ACCESS_ROLES.includes(user.role)) return true;
    const modulos = user.modulos_permitidos || [];
    return modulos.includes(moduleKey);
  }, [user]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: authenticated,
    isSuperAdmin,
    isAdmin,
    isConteoCiego,
    hasModuleAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
